/**
 * "CSV" in the race header, beside the PDF: the race's laps, pit stops or stints as a CSV file
 * (csv/index.ts), for every car or the focus car alone. Laps for every paid plan; pit stops and
 * stints with the Pit Stops and Strategy tabs (Team). The CSV code loads only on a download.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { AnnotationData, RaceChartData } from "@shared/types";
import { api } from "../../../lib/api";
import { track } from "../../../lib/analytics";
import { carNo } from "../car-number";
import type { PitAnalysisResponse } from "../PitAnalysisTab";
import type { CsvFile } from "./index";

export function DownloadCsvButton({
  raceId,
  data,
  annotations,
  focusNum,
  raceName,
  date,
  files,
  className,
  style,
  compact = false,
  loadPits,
  asOf = null,
}: {
  raceId: string;
  data: RaceChartData;
  annotations: AnnotationData;
  focusNum: number;
  raceName: string;
  date: string | null;
  /** The files this viewer's plan opens. */
  files: CsvFile[];
  className?: string;
  style?: CSSProperties;
  /** A phone: the menu's rows are 44 px. */
  compact?: boolean;
  /** The pit stops as the page's Pit Stops tab has them (Live: built from the live chart); else the race's from the server. */
  loadPits?: () => PitAnalysisResponse;
  /** A live race: the files are a snapshot, "lap 212", said in the menu and the file name. */
  asOf?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<"idle" | "busy" | "failed">("idle");
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (
        e instanceof KeyboardEvent
          ? e.key === "Escape"
          : !wrap.current?.contains(e.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  if (!files.length) return null;
  const focus =
    focusNum > 0 && data.cars[String(focusNum)] ? carNo(data, focusNum) : null;

  const run = async (file: CsvFile, scope: "race" | "focus") => {
    setOpen(false);
    setState("busy");
    try {
      const [csv, { downloadBlob }] = await Promise.all([
        import("./index"),
        import("../pdf/kit"),
      ]);
      const only = scope === "focus" ? [focusNum] : undefined;
      const table =
        file === "laps"
          ? csv.lapsCsv(data, only)
          : file === "stints"
            ? csv.stintsCsv(data, annotations, only)
            : csv.pitStopsCsv(
                data,
                annotations,
                loadPits
                  ? loadPits()
                  : await api.get<PitAnalysisResponse>(`/races/${raceId}/pit-analysis`),
                only
              );
      const blob = new Blob([csv.toCsv(table.header, table.rows)], {
        type: "text/csv;charset=utf-8",
      });
      downloadBlob(
        blob,
        csv.csvFileName(
          file,
          raceName,
          date,
          scope === "focus" && focus ? focus : undefined,
          asOf
        )
      );
      track("download_csv", { file, scope, live: asOf ? 1 : 0 });
      setState("idle");
    } catch (e) {
      console.error("CSV download failed", e);
      setState("failed");
      window.setTimeout(() => setState("idle"), 4000);
    }
  };

  const TITLE: Record<CsvFile, string> = {
    laps: "Laps",
    pitstops: "Pit stops",
    stints: "Stints",
  };
  const TIP: Record<CsvFile, string> = {
    laps: "Every lap: lap time, overall and class position, flag, pit; sectors and top speed when the timing gives them",
    pitstops:
      "Every pit stop as the Pit Stops tab has it: in-lap, pit-lane time, time lost, class places, drivers",
    stints:
      "Every stint as the Strategy tab has it: laps, green pace, fall-off, class positions in and out",
  };
  const item = `block w-full px-3 text-left hover:bg-white/10 ${compact ? "min-h-[44px] py-2.5" : "py-2"}`;
  return (
    <div ref={wrap} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={state === "busy"}
        className={className}
        style={{ ...style, opacity: state === "busy" ? 0.6 : 1 }}
        title="Download the race's laps, pit stops or stints as a CSV file (Excel, Google Sheets)"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-live="polite"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v11m0 0l-4-4m4 4l4-4M5 19h14"
          />
        </svg>
        {state === "busy"
          ? "Preparing…"
          : state === "failed"
            ? "CSV failed, try again"
            : "CSV"}
        {state === "idle" && <span aria-hidden>▾</span>}
      </button>
      {open && (
        <div
          role="menu"
          className={`${compact ? "absolute left-0 right-0" : "absolute right-0 min-w-[220px]"} top-full z-30 mt-1 overflow-hidden rounded-md border py-1 text-sm shadow-lg`}
          style={{
            background: (style?.background as string) ?? "#12122a",
            borderColor: (style?.borderColor as string) ?? "#3a3a5c",
          }}
        >
          {asOf && (
            <div className="px-3 pb-1 pt-1.5 text-xs" style={{ color: "#9a9ab4" }}>
              As of {asOf}
            </div>
          )}
          {files.map((f) => (
            <div key={f}>
              <button
                type="button"
                role="menuitem"
                title={`${TIP[f]}: every car`}
                onClick={() => run(f, "race")}
                className={item}
                style={{ color: (style?.color as string) ?? "#e0e0e8" }}
              >
                {TITLE[f]}: every car
              </button>
              {focus && (
                <button
                  type="button"
                  role="menuitem"
                  title={`${TIP[f]}: #${focus} only`}
                  onClick={() => run(f, "focus")}
                  className={item}
                  style={{ color: (style?.color as string) ?? "#e0e0e8" }}
                >
                  {TITLE[f]}: #{focus} only
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
